import React, { useMemo } from 'react';

import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';

import styles from './ChangeRateIndicator.module.css';

export type ChangeRateType = 'positive' | 'negative' | 'neutral';

export interface ChangeRateIndicatorProps {
  value: string;
  type?: ChangeRateType;
  showIcon?: boolean;
  animated?: boolean;
  className?: string;
}

const getChangeRateType = (value: string): ChangeRateType => {
  const numericValue = parseFloat(value.replace(/[^-+\d.]/g, ''));
  if (numericValue > 0) return 'positive';
  if (numericValue < 0) return 'negative';
  return 'neutral';
};

export const ChangeRateIndicator: React.FC<ChangeRateIndicatorProps> = ({
  value,
  type,
  showIcon = true,
  animated = true,
  className,
}) => {
  const changeRateType = type || getChangeRateType(value);

  const isNeutral = useMemo(() => changeRateType === 'neutral', [changeRateType]);

  const getIcon = () => {
    if (!showIcon) return null;

    switch (changeRateType) {
      case 'positive':
        return <ArrowUpOutlined style={{ fontSize: '12px' }} />;
      case 'negative':
        return <ArrowDownOutlined style={{ fontSize: '12px' }} />;
      default:
        return null;
    }
  };

  const getClassName = () => {
    const baseClass = styles.changeRateIndicator;
    const typeClass = (() => {
      switch (changeRateType) {
        case 'positive':
          return styles.changeRatePositive;
        case 'negative':
          return styles.changeRateNegative;
        default:
          return styles.changeRateNeutral;
      }
    })();

    return `${baseClass} ${typeClass} ${className || ''}`.trim();
  };

  const getIconClassName = () => {
    if (!animated) return styles.changeRateIcon;

    switch (changeRateType) {
      case 'positive':
        return `${styles.changeRateIcon} ${styles.changeRateIconPositive}`;
      case 'negative':
        return `${styles.changeRateIcon} ${styles.changeRateIconNegative}`;
      default:
        return styles.changeRateIcon;
    }
  };

  return (
    <span className={getClassName()}>
      {!isNeutral && showIcon && <span className={getIconClassName()}>{getIcon()}</span>}
      {value}
    </span>
  );
};
